/**
 * Text input, with its label and error bound to it.
 *
 * The label and error are part of the component rather than left to the caller, because the
 * accessible wiring — `htmlFor`, `aria-describedby`, `aria-invalid` — is exactly what gets omitted
 * when each screen assembles its own. An input whose error is announced only visually is one a
 * screen-reader user cannot correct.
 */

import { forwardRef, useId, type InputHTMLAttributes } from "react";

import { cx } from "./cx";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string;
  /** Shown under the field and announced. Presence also marks the field invalid. */
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, className, id, ...rest },
  ref,
) {
  const generated = useId();
  const inputId = id ?? generated;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-label-md text-textPrimary">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={cx(hintId, errorId) || undefined}
        className={cx(
          "rounded-sm border bg-surfaceRaised px-3 py-2 text-body-md text-textPrimary",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
          error ? "border-statusDanger" : "border-borderStrong",
          className,
        )}
        {...rest}
      />
      {hint ? (
        <span id={hintId} className="text-label-md text-textMuted">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} role="alert" className="text-label-md text-statusDanger">
          {error}
        </span>
      ) : null}
    </div>
  );
});
